import { apiClient } from './apiClient';
import { GuestEventHistoryEntry } from './analyticsService';

// Types based on backend API documentation
// CompanionGuestEvent interface (Phase 3+)
export interface CompanionGuestEvent {
  id: string;
  invitation_token: string;
  guest_id: string;
  event_id: string;
  guest_name: string;
  nickname?: string;
  display_name: string;
  guest_phone?: string;
  guest_email?: string;
  status: 'pending' | 'accepted' | 'rejected' | 'ignored';
  relationship: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
  added_by: 'primary_guest' | 'host' | 'admin';
  notes?: string;
  companion_metadata?: {
    relationship: string;
    added_by: string;
    notes?: string;
  };
  is_companion: boolean;
  primary_guest_event?: string;
  response_at?: string;
  invite_sent_at?: string;
  /** Who answered this companion's RSVP. Null until somebody has. */
  answered_by?: CompanionAnswerAttribution | null;
  created_at: string;
  updated_at: string;
}

export interface GuestEvent {
  id: number;
  status: 'pending' | 'accepted' | 'rejected' | 'ignored';
  companion_count: number;
  empty_companion_count?: number; // Number of companions without details (count-only)
  empty_companion_statuses?: Array<{index: number; status: 'pending' | 'accepted' | 'rejected' | 'ignored'}>; // Status tracking for empty companions
  companion_limit?: number;
  allow_companions?: boolean;
  companion_guest_events?: CompanionGuestEvent[]; // Companion GuestEvent records
  is_companion?: boolean; // Indicates if this GuestEvent is a companion
  primary_guest_event?: string; // ID of primary guest event (if this is a companion)
  companion_metadata?: {
    relationship: string;
    added_by: string;
    notes?: string;
  };
  total_attendees: number;
  guest_name?: string; // Display name (nickname-aware for hosts, real name otherwise)
  guest_phone?: string; // Guest phone number
  guest: {
    id: string;
    title?: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone?: string;
    role?: string;
  };
  created_by?: {
    id: string;
    email?: string;
    phone?: string;
    first_name: string;
    last_name: string;
    role?: string;
  };
  created_at?: string;
  event_specific_data?: Record<string, any>;
  imported_from_file?: string;
  import_batch_id?: string;
  import_timestamp?: string;
  original_row_number?: number;
  invite_sent_at?: string;
  response_at?: string;
  can_edit_response?: boolean;
  is_imported?: boolean;
  whatsapp_status?: string; // Twilio: queued, sending, sent, delivered, read, failed, undelivered
  whatsapp_sent_at?: string;
  whatsapp_message_sid?: string;
  whatsapp_message_count?: number; // Number of WhatsApp messages sent to this guest
  save_the_date_message_count?: number; // Number of "Save the date" messages sent to this guest
  image_message_count?: number; // Number of image messages sent to this guest
  image_message_sid?: string; // Twilio SID for last image message
  image_message_status?: string; // Delivery status: queued, sending, sent, delivered, read, failed, undelivered
  base_fee_charged?: boolean; // Whether the guest base fee was charged
  base_fee_amount?: string; // Amount of the guest base fee charged
  host_comments?: string;
  host_comments_updated_at?: string;
  host_comments_updated_by?: string;
  host_comments_updated_by_name?: string;
  admin_comments?: string;
  admin_comments_updated_at?: string;
  admin_comments_updated_by?: string;
  admin_comments_updated_by_name?: string;
  invitation_language?: 'arabic' | 'english';
  seat?: string;
}

// Grouped invitation data structure (Phase 3+)
export interface GroupedInvitationData {
  primary_guest: {
    guest_event_id: string;
    invitation_token: string;
    guest_id: string;
    guest_name: string;
    nickname?: string;
    display_name: string;
    guest_phone?: string;
    status: 'pending' | 'accepted' | 'rejected' | 'ignored';
    is_companion: false;
  };
  companions: CompanionGuestEvent[];
  is_companion_invitation: boolean; // Indicates if this token belongs to a companion
}

// Invitation verification response (Phase 3+)
export interface InvitationVerificationResponse {
  success: boolean;
  event: {
    event_id: string;
    event_title: string;
    description?: string;
    event_date: string;
    event_time: string;
    location?: string;
    host_name: string;
    requires_verification: boolean;
    allow_companions: boolean;
    companion_limit?: number;
    guest_limit?: number;
    message_template?: string;
    editable_until?: string;
    link_expiry?: string;
    background_theme?: string;
    custom_gif_url?: string;
    custom_background_color?: string;
    image_url?: string;
    image_shape?: string;
    location_link?: string;
    website_link?: string;
    dress_code?: string;
    contact_mirsal_link?: string;
    total_guests_invited: number;
    primary_guest: {
      guest_event_id: string;
      invitation_token: string;
      guest_id: string;
      guest_name: string;
      nickname?: string;
      display_name: string;
      guest_phone?: string;
      status: 'pending' | 'accepted' | 'rejected' | 'ignored';
      is_companion: false;
    };
    companions: CompanionGuestEvent[];
    is_companion_invitation: boolean;
  };
}

export interface GuestListResponse {
  success: boolean;
  results: GuestEvent[];
  count: number;
  next?: string | null;
  previous?: string | null;
  summary: {
    total_invited: number;
    total_attendees: number;
    status_breakdown: {
      pending: number;
      accepted: number;
      rejected: number;
      ignored: number;
    };
    response_rate: number;
    imported_guests: number;
    guest_limit?: number;
    remaining_spots?: number;
  };
}

export interface GuestCreateData {
  guest_id?: string;
  guest_phone?: string;
  guest_email?: string;
  guest_title?: string;
  guest_first_name?: string;
  guest_last_name?: string;
  companion_name?: string;
  companion_count?: number;
  empty_companion_count?: number; // Number of empty companions (count-only)
  companion_limit?: number;
  allow_companions?: boolean;
  country_code?: string;
  invitation_language?: 'arabic' | 'english';
  seat?: string;
}

export interface GuestUpdateData {
  status?: 'pending' | 'accepted' | 'rejected' | 'ignored';
  companion_name?: string;
  companion_count?: number;
  companion_limit?: number;
  allow_companions?: boolean;
  empty_companion_count?: number;
  empty_companion_statuses?: Array<{index: number; status: 'pending' | 'accepted' | 'rejected' | 'ignored'}>;
}

export interface GuestListParams {
  status?: 'pending' | 'accepted' | 'rejected' | 'ignored' | ('pending' | 'accepted' | 'rejected' | 'ignored')[];
  search?: string;
  import_batch_id?: string;
  imported_only?: boolean;
  manual_only?: boolean;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface BulkActionData {
  action: 'delete' | 'update_status' | 'send_invites' | 'export';
  guest_ids: number[];
  new_status?: string;
  export_format?: 'csv' | 'excel';
}

export interface GuestImportBatch {
  id: string;
  filename: string;
  total_rows: number;
  successful_imports: number;
  failed_imports: number;
  import_status: 'processing' | 'completed' | 'failed';
  created_at: string;
}

export interface ImportError {
  id: number;
  row_number: number;
  error_type: 'duplicate_phone' | 'invalid_phone' | 'missing_name' | 'invalid_email' | 'system_error';
  error_message: string;
  row_data: Record<string, any>;
}

export interface CompanionCreateData {
  name: string;
  phone?: string;
  email?: string;
  relationship: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
  notes?: string;
}

export interface CompanionUpdateData {
  name?: string;
  phone?: string;
  email?: string;
  relationship?: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
  notes?: string;
}

// New CompanionGuestEventCreateData interface (Phase 3+)
export interface CompanionGuestEventCreateData {
  first_name: string;
  last_name?: string;
  phone?: string;
  email?: string;
  nickname?: string;
  relationship: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
  notes?: string;
}

// New CompanionGuestEventUpdateData interface (Phase 3+)
export interface CompanionGuestEventUpdateData {
  status?: 'pending' | 'accepted' | 'rejected' | 'ignored';
  nickname?: string;
  relationship?: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
  notes?: string;
  phone?: string;
  email?: string;
}

export interface ImportResponse {
  success: boolean;
  message: string;
  data?: {
    import_batch: GuestImportBatch;
    results: {
      total_processed: number;
      successful: number;
      failed: number;
      duplicates: number;
      users_created?: number;
      error_summary?: string[];
    };
    error_details?: ValidationError[];
  };
  preview?: {
    total_rows: number;
    sample_data: any[];
    column_mapping: Record<string, string>;
    validation_passed: boolean;
    validation_errors?: ValidationError[];
  };
  validation_errors?: ValidationError[];
}

export interface ValidationError {
  row_number: number;
  error_type: string;
  error_message: string;
  row_data: Record<string, any>;
}

// Preview Import Types
// Index signature allows backend columns like "companion 1 name", "companion 2 phone", etc.
export interface PreviewRowParsedData {
  full_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  title: string;
  companion_name: string;
  companion_phone: string;
  relationship: string;
  notes: string;
  [key: string]: string | undefined;
}

export interface PreviewRowValidation {
  is_valid: boolean;
  errors: string[];
  warnings: string[];
  error_fields: string[];
  warning_fields: string[];
}

export interface PreviewRow {
  row_number: number;
  original_data: Record<string, string>;
  parsed_data: PreviewRowParsedData;
  validation: PreviewRowValidation;
  is_duplicate: boolean;
  selected: boolean;
}

export interface PreviewImportResponse {
  success: boolean;
  message: string;
  data?: {
    rows: PreviewRow[];
    summary: {
      total_rows: number;
      valid_rows: number;
      invalid_rows: number;
      duplicate_rows: number;
    };
    columns: string[];
  };
  error?: {
    type: string;
    message: string;
    details?: any;
  };
}

export interface ConfirmImportResponse {
  success: boolean;
  message: string;
  data?: {
    results: {
      successful: number;
      failed: number;
      skipped_duplicates: number;
      total_processed: number;
      error_details: Array<{ row_number: number; error: string }>;
      created_guests: Array<{ row_number: number; name: string; phone: string; guest_event_id: number }>;
    };
    import_batch_id: string;
  };
  error?: {
    type: string;
    message: string;
  };
}

export interface ValidateRowResponse {
  success: boolean;
  validation: PreviewRowValidation;
  parsed_data: PreviewRowParsedData;
  error?: string;
}

/** Who recorded a companion's RSVP, and the answer it replaced (if any). */
export interface CompanionAnswerAttribution {
  source: 'companion' | 'primary_guest' | 'host' | 'admin';
  name: string;
  status: string;
  at: string;
  /** The most recent superseded answer, when this one overrode another. */
  previous?: Array<{ source: string; name: string; status: string; at: string }>;
}

/**
 * One row in the follow-up confirmation table.
 *
 * `question` rows are not a message to the named person - they ask the primary guest to
 * confirm a companion, which is the only way to reach a companion with no phone.
 * `guest_event_id` for those is a synthetic `question:<id>` key, not a real GuestEvent id.
 */
export interface FollowUpRecipient {
  guest_event_id: string;
  name: string;
  type: 'guest' | 'companion' | 'question';
  /** The primary guest's GuestEvent id - rows are grouped under it in the table. */
  primary_guest_event_id: string;
  primary_guest_name: string;
  phone: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'ignored';
}

/** Shared response shape for the reminder and follow-up bulk sends. */
export interface ReminderSendResponse {
  success: boolean;
  partial?: boolean;
  all_failed?: boolean;
  message: string;
  results: {
    total_sent: number;
    successful: number;
    failed: number;
    details: Array<{
      guest_id: string;
      guest_event_id: string;
      phone: string;
      name: string;
      result: {
        success: boolean;
        message_sid?: string;
        status?: string;
        error?: string;
        error_code?: string;
        to?: string;
        date_created?: string;
      };
    }>;
    errors?: Array<{
      guest_id: string;
      phone: string;
      name: string;
      error: string;
    }>;
  };
}

export const guestAPI = {
  // Get guest list with filtering and statistics
  getGuests: async (eventId: string, params?: GuestListParams): Promise<GuestListResponse> => {
    const queryParams = new URLSearchParams();
    
    // Handle multiple status filters
    if (params?.status) {
      if (Array.isArray(params.status)) {
        params.status.forEach(status => queryParams.append('status', status));
      } else {
        queryParams.append('status', params.status);
      }
    }
    if (params?.search) queryParams.append('search', params.search);
    if (params?.import_batch_id) queryParams.append('import_batch_id', params.import_batch_id);
    if (params?.imported_only) queryParams.append('imported_only', params.imported_only.toString());
    if (params?.manual_only) queryParams.append('manual_only', params.manual_only.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.ordering) queryParams.append('ordering', params.ordering);

    // Use cache: false for guest list to ensure fresh data, especially after responses
    const response = await apiClient.get<GuestListResponse>(
      `/events/${eventId}/guests/?${queryParams.toString()}`,
      { cache: false }
    );
    return response.data;
  },

  /** Fetch every guest-event ID for an event, respecting filters (paginates through all pages). */
  getAllGuestEventIds: async (
    eventId: string,
    params?: Omit<GuestListParams, 'page' | 'page_size'>
  ): Promise<string[]> => {
    const allIds: string[] = [];
    const pageSize = 100;
    let page = 1;

    while (true) {
      const response = await guestAPI.getGuests(eventId, { ...params, page, page_size: pageSize });
      allIds.push(...response.results.map((g) => g.id.toString()));

      // Follow `next` when present; otherwise stop when a short/empty page is returned.
      const fetchedCount = response.results.length;
      const expectedTotal = response.count ?? 0;
      if (response.next) {
        page += 1;
        continue;
      }
      if (fetchedCount === 0 || allIds.length >= expectedTotal) {
        break;
      }
      // Backend may ignore page_size — keep paging until we have every guest.
      page += 1;
    }

    return allIds;
  },

  /** Resolve guest-event records by ID, paginating the full event guest list (ignores active filters). */
  resolveSelectedGuestEvents: async (
    eventId: string,
    guestEventIds: string[]
  ): Promise<GuestEvent[]> => {
    if (guestEventIds.length === 0) return [];

    const needed = new Set(guestEventIds.map(String));
    const found = new Map<string, GuestEvent>();
    const pageSize = 100;
    let page = 1;

    while (found.size < needed.size) {
      const response = await guestAPI.getGuests(eventId, { page, page_size: pageSize });
      for (const guest of response.results) {
        const id = guest.id.toString();
        if (needed.has(id)) {
          found.set(id, guest);
        }
      }
      if (response.results.length === 0) break;
      if (found.size >= needed.size) break;
      if (found.size >= (response.count ?? 0)) break;
      if (response.next) {
        page += 1;
        continue;
      }
      page += 1;
    }

    return guestEventIds
      .map((id) => found.get(String(id)))
      .filter((guest): guest is GuestEvent => guest != null);
  },

  // Get individual guest details
  getGuest: async (eventId: string, guestId: number): Promise<GuestEvent> => {
    const response = await apiClient.get<{ success: boolean; data: GuestEvent }>(
      `/events/${eventId}/guests/${guestId}/`
    );
    return response.data.data;
  },

  // Create new guest invitation
  createGuest: async (eventId: string, guestData: GuestCreateData): Promise<GuestEvent> => {
    const response = await apiClient.post<{ success: boolean; data: GuestEvent }>(
      `/events/${eventId}/guests/`,
      guestData
    );
    return response.data.data;
  },

  // Update guest information
  updateGuest: async (eventId: string, guestId: number, guestData: Partial<GuestCreateData>): Promise<GuestEvent> => {
    const response = await apiClient.put<{ success: boolean; data: GuestEvent }>(
      `/events/${eventId}/guests/${guestId}/`,
      guestData
    );
    return response.data.data;
  },

  // Update guest status
  updateGuestStatus: async (eventId: string, guestId: number, statusData: GuestUpdateData): Promise<GuestEvent> => {
    const response = await apiClient.post<{ success: boolean; data: GuestEvent }>(
      `/events/${eventId}/guests/${guestId}/update-status/`,
      statusData
    );
    return response.data.data;
  },

  // Delete guest
  deleteGuest: async (eventId: string, guestId: number): Promise<void> => {
    await apiClient.delete(`/events/${eventId}/guests/${guestId}/`);
    // Invalidate cache
    apiClient.invalidateCache(`/events/${eventId}/guests/`);
  },

  // Send invitation to guest
  sendInvitation: async (eventId: string, guestId: number): Promise<{ 
    success: boolean; 
    message: string; 
  }> => {
    const response = await apiClient.post<{ 
      success: boolean; 
      message: string; 
    }>(`/events/${eventId}/guests/${guestId}/send-invitation/`);
    
    // Invalidate cache after sending invitation
    apiClient.invalidateCache(`/events/${eventId}/guests/`);
    
    return response.data;
  },

  // Bulk send invitations
  bulkSendInvitations: async (eventId: string, guestIds: string[]): Promise<{ 
    success: boolean; 
    message: string; 
    affected_count: number;
    guests_with_companions?: Array<{
      guest_id: string;
      guest_name: string;
      companion_count: number;
      status: string;
    }>;
    total_companions?: number;
  }> => {
    const response = await apiClient.post<{ 
      success: boolean; 
      message: string; 
      affected_count: number;
      guests_with_companions?: Array<{
        guest_id: string;
        guest_name: string;
        companion_count: number;
        status: string;
      }>;
      total_companions?: number;
    }>(`/events/${eventId}/guests/bulk-send-invitations/`, {
      guest_ids: guestIds
    });
    
    // Invalidate cache after bulk operations
    apiClient.invalidateCache(`/events/${eventId}/guests/`);
    
    return response.data;
  },

  // Bulk actions on guests
  bulkAction: async (eventId: string, actionData: BulkActionData): Promise<{ 
    success: boolean; 
    message: string; 
    affected_count: number; 
    export_data?: any[] 
  }> => {
    const response = await apiClient.post<{ 
      success: boolean; 
      message: string; 
      affected_count: number; 
      export_data?: any[] 
    }>(`/events/${eventId}/guests/bulk-actions/`, actionData);
    
    // Invalidate cache after bulk operations
    apiClient.invalidateCache(`/events/${eventId}/guests/`);
    
    return response.data;
  },

  // Import guests from file
  importGuests: async (
    eventId: string, 
    file: File, 
    options?: {
      skip_duplicates?: boolean;
      send_invites?: boolean;
      validate_only?: boolean;
      column_mapping?: Record<string, string>;
    }
  ): Promise<ImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    
    if (options?.skip_duplicates !== undefined) {
      formData.append('skip_duplicates', options.skip_duplicates.toString());
    }
    if (options?.send_invites !== undefined) {
      formData.append('send_invites', options.send_invites.toString());
    }
    if (options?.validate_only !== undefined) {
      formData.append('validate_only', options.validate_only.toString());
    }
    if (options?.column_mapping) {
      formData.append('column_mapping', JSON.stringify(options.column_mapping));
    }

    const response = await apiClient.post<ImportResponse>(
      `/events/${eventId}/import/upload/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    // Invalidate cache after import
    apiClient.invalidateCache(`/events/${eventId}/guests/`);

    return response.data;
  },

  // Preview import file data before actual import
  previewImport: async (
    eventId: string,
    file: File
  ): Promise<PreviewImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<PreviewImportResponse>(
      `/events/${eventId}/import/preview-import/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },

  // Validate a single row of import data
  validateRow: async (
    eventId: string,
    parsedData: PreviewRowParsedData
  ): Promise<ValidateRowResponse> => {
    const response = await apiClient.post<ValidateRowResponse>(
      `/events/${eventId}/import/validate-row/`,
      { parsed_data: parsedData }
    );
    return response.data;
  },

  // Confirm import with edited preview data
  confirmImport: async (
    eventId: string,
    rows: PreviewRow[],
    options?: {
      skip_duplicates?: boolean;
      send_invites?: boolean;
    }
  ): Promise<ConfirmImportResponse> => {
    const response = await apiClient.post<ConfirmImportResponse>(
      `/events/${eventId}/import/confirm-import/`,
      {
        rows,
        skip_duplicates: options?.skip_duplicates ?? true,
        send_invites: options?.send_invites ?? false,
      }
    );

    // Invalidate cache after import
    apiClient.invalidateCache(`/events/${eventId}/guests/`);

    return response.data;
  },

  // Get import batches
  getImportBatches: async (eventId: string): Promise<{
    success: boolean;
    results: GuestImportBatch[];
    count: number;
  }> => {
    const response = await apiClient.get<{
      success: boolean;
      results: GuestImportBatch[];
      count: number;
    }>(`/events/${eventId}/import/`);
    return response.data;
  },

  // Get import batch status
  getImportStatus: async (eventId: string, batchId: string): Promise<{
    success: boolean;
    data: {
      batch: GuestImportBatch;
      errors: ImportError[];
      progress: {
        percentage: number;
        processed: number;
        total: number;
      };
      can_retry: boolean;
    };
  }> => {
    const response = await apiClient.get<{
      success: boolean;
      data: {
        batch: GuestImportBatch;
        errors: ImportError[];
        progress: {
          percentage: number;
          processed: number;
          total: number;
        };
        can_retry: boolean;
      };
    }>(`/events/${eventId}/import/${batchId}/status/`);
    return response.data;
  },

  // Retry failed imports
  retryImport: async (
    eventId: string, 
    batchId: string, 
    options?: {
      error_ids?: number[];
      skip_duplicates?: boolean;
      send_invites?: boolean;
    }
  ): Promise<{
    success: boolean;
    message: string;
    data: {
      results: {
        successful: number;
        failed: number;
      };
      updated_batch: GuestImportBatch;
    };
  }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: {
        results: {
          successful: number;
          failed: number;
        };
        updated_batch: GuestImportBatch;
      };
    }>(`/events/${eventId}/import/${batchId}/retry/`, options || {});

    // Invalidate cache after retry
    apiClient.invalidateCache(`/events/${eventId}/guests/`);

    return response.data;
  },

  // Get previous guests the host has invited
  getPreviousGuests: async (eventId: string, search?: string): Promise<{
    success: boolean;
    guests: Array<{
      id: string;
      first_name: string;
      last_name: string;
      display_name: string;
      last_invited_date?: string;
    }>;
    count: number;
  }> => {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await apiClient.get(
      `/events/${eventId}/guests/previous-guests${params}`
    );
    return response.data;
  },

  // Lookup existing guest by phone number
  lookupGuestByPhone: async (eventId: string, phone: string, countryCode?: string): Promise<{
    success: boolean;
    found: boolean;
    access_denied?: boolean;
    reason?: string;
    data?: {
      id: number;
      first_name: string;
      last_name: string;
      display_name?: string; // Nickname-aware display name (nickname if available, otherwise real name)
      phone: string;
      email: string;
      already_invited: boolean;
      existing_invitation_id?: number;
      has_nickname?: boolean;
      nickname?: string;
      access_reason?: string;
    };
    message?: string;
    error?: {
      type: string;
      message: string;
    };
  }> => {
    // Build query params
    const params = new URLSearchParams();
    params.append('phone', phone);
    if (countryCode) {
      params.append('country_code', countryCode);
    }
    
    const response = await apiClient.get(
      `/events/${eventId}/guests/lookup-by-phone/?${params.toString()}`
    );
    return response.data;
  },

  // Guest response to invitation (accept/decline) - Updated for Phase 3+
  respondToInvitation: async (
    token: string, 
    status: 'accepted' | 'rejected' | 'pending',
    companionResponses?: Array<{
      guest_event_id: string;
      status: 'accepted' | 'rejected' | 'pending';
    }>
  ): Promise<{
    success: boolean;
    message: string;
    event: InvitationVerificationResponse['event'];
  }> => {
    const requestData: any = {
      status: status
    };
    
    // Add companion responses if provided (for bulk updates)
    if (companionResponses && companionResponses.length > 0) {
      requestData.companion_responses = companionResponses;
    }
    
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      event: InvitationVerificationResponse['event'];
    }>(`/invitation/${token}/respond/`, requestData);
    
    // Invalidate caches to ensure fresh data on next fetch
    // Invalidate dashboard cache
    apiClient.invalidateCache('dashboard');
    // Invalidate guest list cache for the event (if event_id is available in response)
    if (response.data?.event?.event_id) {
      apiClient.invalidateCache(`events/${response.data.event.event_id}/guests`);
    }
    // Also invalidate all guest-related caches
    apiClient.invalidateCache('guests');
    apiClient.invalidateCache('events');
    
    return response.data;
  },

  // Verify invitation token (Phase 3+)
  verifyInvitationToken: async (token: string): Promise<InvitationVerificationResponse> => {
    const response = await apiClient.get<InvitationVerificationResponse>(
      `/invitation/${token}/`
    );
    return response.data;
  },

  // Send WhatsApp invitations to selected guests
  sendWhatsAppInvitations: async (eventId: string, guestIds: string[], sendByName: boolean = true): Promise<{
    success: boolean;
    partial?: boolean;
    all_failed?: boolean;
    message: string;
    stats?: {
      total: number;
      successful: number;
      failed: number;
    };
    data: {
      total_sent: number;
      successful: number;
      failed: number;
      results: Array<{
        guest_id: string;
        guest_event_id?: string;
        phone: string;
        name: string;
        result: {
          success: boolean;
          message_sid?: string;
          status?: string;
          error?: string;
          error_code?: string;
          to?: string;
          date_created?: string;
        };
      }>;
      errors?: Array<{
        guest_id: string;
        phone: string;
        name: string;
        error: string;
      }>;
    };
  }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      data: {
        total_sent: number;
        successful: number;
        failed: number;
        results: Array<{
          guest_id: string;
          guest_event_id?: string;
          phone: string;
          name: string;
          result: {
            success: boolean;
            message_sid?: string;
            status?: string;
            error?: string;
            error_code?: string;
            to?: string;
            date_created?: string;
          };
        }>;
        errors?: Array<{
          guest_id: string;
          phone: string;
          name: string;
          error: string;
        }>;
      };
    }>(`/events/${eventId}/guests/send-whatsapp-invitations/`, {
      guest_ids: guestIds,
      send_by_name: sendByName
    });
    
    // Invalidate dashboard cache to ensure fresh data on next fetch
    apiClient.invalidateCache('dashboard');
    
    return response.data;
  },

  // Send reminder messages to selected guests
  sendReminders: async (eventId: string, guestIds: string[], sendByName: boolean = true): Promise<ReminderSendResponse> => {
    const response = await apiClient.post<ReminderSendResponse>(`/events/${eventId}/send-reminders/`, {
      guest_ids: guestIds,
      send_by_name: sendByName
    });

    // Invalidate dashboard cache to ensure fresh data on next fetch
    apiClient.invalidateCache('dashboard');

    return response.data;
  },

  // Resolve exactly who a follow-up would reach, without sending anything.
  // Backs the confirmation table so it always matches the real send.
  previewFollowUps: async (eventId: string, guestIds: string[]): Promise<FollowUpRecipient[]> => {
    const response = await apiClient.post<{ success: boolean; dry_run: boolean; recipients: FollowUpRecipient[] }>(
      `/events/${eventId}/send-follow-ups/`,
      { guest_ids: guestIds, dry_run: true }
    );
    return response.data.recipients || [];
  },

  // Send a follow-up only to guests/companions in the selection who have not responded yet.
  // Anyone who already accepted or declined is skipped by the backend, and anything in
  // excludeGuestEventIds (rows the host unticked) is skipped on top of that.
  sendFollowUps: async (
    eventId: string,
    guestIds: string[],
    sendByName: boolean = true,
    excludeGuestEventIds: string[] = []
  ): Promise<ReminderSendResponse> => {
    const response = await apiClient.post<ReminderSendResponse>(`/events/${eventId}/send-follow-ups/`, {
      guest_ids: guestIds,
      send_by_name: sendByName,
      exclude_guest_event_ids: excludeGuestEventIds
    });

    // Invalidate dashboard cache to ensure fresh data on next fetch
    apiClient.invalidateCache('dashboard');

    return response.data;
  },

  // Send reminder message to a specific guest
  sendReminderToGuest: async (eventId: string, guestId: string): Promise<{
    success: boolean;
    message: string;
    result: {
      success: boolean;
      message_sid?: string;
      status?: string;
      error?: string;
      error_code?: string;
      to?: string;
      date_created?: string;
    };
  }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      result: {
        success: boolean;
        message_sid?: string;
        status?: string;
        error?: string;
        error_code?: string;
        to?: string;
        date_created?: string;
      };
    }>(`/events/${eventId}/guests/${guestId}/send-reminder/`);
    
    // Invalidate dashboard cache to ensure fresh data on next fetch
    apiClient.invalidateCache('dashboard');
    
    return response.data;
  },

  // Send "Save the date" messages to selected guests
  sendSaveTheDate: async (eventId: string, guestIds: string[]): Promise<{
    success: boolean;
    partial?: boolean;
    all_failed?: boolean;
    message: string;
    results: {
      total_sent: number;
      successful: number;
      failed: number;
      details: Array<{
        guest_id: string;
        guest_event_id: string;
        phone: string;
        name: string;
        result: {
          success: boolean;
          message_sid?: string;
          status?: string;
          error?: string;
          error_code?: string;
          to?: string;
          date_created?: string;
        };
      }>;
      errors?: Array<{
        guest_id: string;
        phone: string;
        name: string;
        error: string;
      }>;
    };
  }> => {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
      results: {
        total_sent: number;
        successful: number;
        failed: number;
        details: Array<{
          guest_id: string;
          guest_event_id: string;
          phone: string;
          name: string;
          result: {
            success: boolean;
            message_sid?: string;
            status?: string;
            error?: string;
            error_code?: string;
            to?: string;
            date_created?: string;
          };
        }>;
        errors?: Array<{
          guest_id: string;
          phone: string;
          name: string;
          error: string;
        }>;
      };
    }>(`/events/${eventId}/send-save-the-date/`, {
      guest_ids: guestIds
    });
    
    // Invalidate dashboard cache to ensure fresh data on next fetch
    apiClient.invalidateCache('dashboard');
    
    return response.data;
  },

  // Get individual guest invitation link
  getGuestInvitationLink: async (eventId: string, guestId: string): Promise<{ invitation_link: string }> => {
  const response = await apiClient.get<{ success: boolean; data: { invitation_link: string } }>(
    `/events/${eventId}/guests/${guestId}/invitation-link/`
  );
  return response.data.data;
},

// Get guest display name (with nickname if available)
getGuestDisplayName: async (guestId: string): Promise<{
  success: boolean;
  guest: {
    id: string;
    real_name: string;
    phone?: string;
    email?: string;
  };
  display_name: string;
  is_nickname?: boolean;
  nicknames?: Array<{
    host_name: string;
    nickname: string;
    created_at: string;
  }>;
}> => {
  const response = await apiClient.get<{
    success: boolean;
    guest: {
      id: string;
      real_name: string;
      phone?: string;
      email?: string;
    };
    display_name: string;
    is_nickname?: boolean;
    nicknames?: Array<{
      host_name: string;
      nickname: string;
      created_at: string;
    }>;
  }>(`/guests/${guestId}/display-name/`);
  return response.data;
},

  // Get companion history for a guest
  getCompanionHistory: async (guestId: string, page: number = 1, limit: number = 20): Promise<{
    guest: {
      id: string;
      first_name: string;
      last_name: string;
      email?: string;
      phone?: string;
    };
    companions: Array<{
      name: string;
      nickname?: string;
      phone?: string;
      email?: string;
      relationship: 'spouse' | 'partner' | 'child' | 'parent' | 'sibling' | 'friend' | 'colleague' | 'relative' | 'other';
      last_used: string;
      event_count: number;
      events: Array<{
        event_id: string;
        event_title: string;
        event_date: string;
        status: 'accepted' | 'rejected' | 'pending' | 'ignored';
        created_at: string;
        guest_event_id: string;
      }>;
    }>;
    total_companions?: number;
    page?: number;
    limit?: number;
    has_more?: boolean;
  }> => {
    const response = await apiClient.get<{ success: boolean; data: any }>(
      `/guests/${guestId}/companion-history/`,
      { params: { page, limit } }
    );
    return response.data.data;
  },

  // Get current companions for a guest in a specific event
  getCurrentCompanions: async (eventId: string, guestId: number): Promise<CompanionGuestEvent[]> => {
    const response = await apiClient.get<{ success: boolean; data: CompanionGuestEvent[] }>(
      `/events/${eventId}/guests/${guestId}/companions/`
    );
    return response.data.data;
  },

  // Update guest comments
  updateGuestComments: async (eventId: string, guestId: number, comments: string): Promise<GuestEvent> => {
    const response = await apiClient.post<{ success: boolean; data: GuestEvent }>(
      `/events/${eventId}/guests/${guestId}/update-comments/`,
      { comments }
    );
    return response.data.data;
  },

// Companion Management Methods
companion: {
  // Get companions for a specific guest event
  // Get companions (now returns CompanionGuestEvent[])
  getCompanions: async (eventId: string, guestId: number): Promise<CompanionGuestEvent[]> => {
    const response = await apiClient.get<{ count: number; next: string | null; previous: string | null; results: CompanionGuestEvent[] }>(
      `/events/${eventId}/guests/${guestId}/companions/`
    );
    // Return the results array from paginated response
    return response.data.results || [];
  },

  // Create a new companion for a guest event (Phase 3+)
  createCompanion: async (eventId: string, guestId: number, companionData: CompanionGuestEventCreateData): Promise<CompanionGuestEvent> => {
    const response = await apiClient.post<{ success: boolean; data: CompanionGuestEvent }>(
      `/events/${eventId}/guests/${guestId}/companions/`,
      companionData
    );
    // Invalidate companion-history cache since a new companion was added
    // This ensures the guest details modal shows updated companion data
    apiClient.invalidateCache('guests');
    apiClient.invalidateCache('companion-history');
    return response.data.data;
  },

  // Update a companion (Phase 3+)
  updateCompanion: async (eventId: string, guestId: number, companionId: string, companionData: CompanionGuestEventUpdateData): Promise<CompanionGuestEvent> => {
    const response = await apiClient.put<{ success: boolean; data: CompanionGuestEvent }>(
      `/events/${eventId}/guests/${guestId}/companions/${companionId}/`,
      companionData
    );
    // Invalidate companion-history cache since companion was updated
    apiClient.invalidateCache('guests');
    apiClient.invalidateCache('companion-history');
    return response.data.data;
  },

  // Convert empty companion to detailed companion
  convertEmptyCompanion: async (eventId: string, guestId: number, companionData: CompanionGuestEventCreateData): Promise<CompanionGuestEvent> => {
    const response = await apiClient.post<{ success: boolean; data: CompanionGuestEvent }>(
      `/events/${eventId}/guests/${guestId}/companions/convert-empty/`,
      companionData
    );
    // Invalidate companion-history cache since a new companion was added
    apiClient.invalidateCache('guests');
    apiClient.invalidateCache('companion-history');
    return response.data.data;
  },

  // Delete a companion
  deleteCompanion: async (eventId: string, guestId: number, companionId: string): Promise<void> => {
    await apiClient.delete(`/events/${eventId}/guests/${guestId}/companions/${companionId}/`);
    // Invalidate companion-history cache since a companion was deleted
    apiClient.invalidateCache('guests');
    apiClient.invalidateCache('companion-history');
  },

  // Get guest event with companions
  getGuestWithCompanions: async (eventId: string, guestId: number): Promise<GuestEvent> => {
    const response = await apiClient.get<{ success: boolean; data: GuestEvent }>(
      `/events/${eventId}/guests-with-companions/${guestId}/`
    );
    return response.data.data;
  },
},

// Get guest history (top-level method)
getGuestHistory: async (eventId: string, guestId: number): Promise<GuestEventHistoryEntry[]> => {
  const response = await apiClient.get<{ success: boolean; data: GuestEventHistoryEntry[] }>(
    `/events/${eventId}/guests/${guestId}/history/`
  );
  return response.data.data;
},
};
